import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Spinner,
  Badge,
  Text,
  Title1,
  Body1,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle } from '../components/Common/ScrollableMessageBar';
import {
  CheckmarkCircle24Regular,
  ErrorCircle24Regular,
  Warning24Regular,
  ArrowSync24Regular,
  Database24Regular,
  Cloud24Regular,
  Shield24Regular,
  Code24Regular,
  People24Regular,
  Settings24Regular } from
'@fluentui/react-icons';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1400px',
    margin: '0 auto'
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL
  },
  testGrid: {
    display: 'grid',
    gap: tokens.spacingHorizontalL,
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    marginBottom: tokens.spacingVerticalXXL
  },
  testCard: {
    height: '100%'
  },
  testHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  testBody: {
    padding: tokens.spacingHorizontalL
  },
  testItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`
  },
  statusBadge: {
    minWidth: '80px'
  },
  responseBox: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalS,
    fontFamily: 'monospace',
    fontSize: '12px',
    maxHeight: '200px',
    overflow: 'auto'
  },
  actionButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL
  }
});

interface TestResult {
  endpoint: string;
  status: 'pending' | 'testing' | 'success' | 'error' | 'warning';
  message?: string;
  response?: any;
  duration?: number;
}

interface TestCategory {
  name: string;
  icon: any;
  tests: TestResult[];
}

export const SystemDebug: React.FC = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<string>('all');
  const [isTestingAll, setIsTestingAll] = useState(false);
  const [testCategories, setTestCategories] = useState<TestCategory[]>([
  {
    name: 'Authentication & Security',
    icon: Shield24Regular,
    tests: [
    { endpoint: '/api/auth/validate', status: 'pending' },
    { endpoint: '/api/auth/user', status: 'pending' },
    { endpoint: '/api/auth/roles', status: 'pending' }]

  },
  {
    name: 'Teams Integration',
    icon: People24Regular,
    tests: [
    { endpoint: '/api/teams/status', status: 'pending' },
    { endpoint: '/api/teams/groups/search?query=admin', status: 'pending' },
    { endpoint: '/api/teams/federation/status', status: 'pending' },
    { endpoint: '/api/policies', status: 'pending' }]

  },
  {
    name: 'PowerShell Session',
    icon: Code24Regular,
    tests: [
    { endpoint: '/api/teams/powershell/status', status: 'pending' },
    { endpoint: '/api/teams/powershell/commands/test', status: 'pending' },
    { endpoint: '/api/teams/powershell/modules', status: 'pending' }]

  },
  {
    name: 'Domain Feed',
    icon: Cloud24Regular,
    tests: [
    { endpoint: '/api/lseg/domains', status: 'pending' },
    { endpoint: '/api/lseg/statistics', status: 'pending' },
    { endpoint: '/api/lseg/sync-status', status: 'pending' },
    { endpoint: '/api/lseg/feeds', status: 'pending' }]

  },
  {
    name: 'PostgreSQL Database',
    icon: Database24Regular,
    tests: [
    { endpoint: '/api/database/health', status: 'pending' },
    { endpoint: '/api/domains', status: 'pending' },
    { endpoint: '/api/policies', status: 'pending' },
    { endpoint: '/api/audit', status: 'pending' }]

  },
  {
    name: 'Policy Management',
    icon: Settings24Regular,
    tests: [
    { endpoint: '/api/policies', status: 'pending' },
    { endpoint: '/api/policies/gfc-prerequisites', status: 'pending' },
    { endpoint: '/api/policies/existing-teams-policies', status: 'pending' },
    { endpoint: '/api/policies/optimize-domains', status: 'pending' }]

  }]
  );

  const runTest = async (categoryIndex: number, testIndex: number) => {
    const updatedCategories = [...testCategories];
    const test = updatedCategories[categoryIndex].tests[testIndex];

    // Set to testing
    test.status = 'testing';
    setTestCategories([...updatedCategories]);

    const startTime = Date.now();

    try {
      const response = await apiClient.get(test.endpoint);
      const duration = Date.now() - startTime;

      test.status = 'success';
      test.message = `Success (${duration}ms)`;
      test.response = response.data;
      test.duration = duration;
    } catch (error: any) {
      const duration = Date.now() - startTime;

      if (error.response?.status === 401) {
        test.status = 'warning';
        test.message = `Auth required (${duration}ms)`;
      } else if (error.response?.status === 404) {
        test.status = 'error';
        test.message = `Endpoint not found (${duration}ms)`;
      } else {
        test.status = 'error';
        test.message = error.message || `Failed (${duration}ms)`;
      }

      test.response = error.response?.data || error.message;
      test.duration = duration;
    }

    setTestCategories([...updatedCategories]);
  };

  const runAllTests = async () => {
    setIsTestingAll(true);

    for (let i = 0; i < testCategories.length; i++) {
      for (let j = 0; j < testCategories[i].tests.length; j++) {
        await runTest(i, j);
        // Small delay between tests to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    setIsTestingAll(false);
  };

  const runCategoryTests = async (categoryIndex: number) => {
    for (let j = 0; j < testCategories[categoryIndex].tests.length; j++) {
      await runTest(categoryIndex, j);
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  const resetTests = () => {
    const reset = testCategories.map((category) => ({
      ...category,
      tests: category.tests.map((test) => ({
        ...test,
        status: 'pending' as const,
        message: undefined,
        response: undefined,
        duration: undefined
      }))
    }));
    setTestCategories(reset);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckmarkCircle24Regular primaryFill={tokens.colorPaletteGreenForeground1} data-testid="system-debug-checkmark-circle24-regular" />;
      case 'error':
        return <ErrorCircle24Regular primaryFill={tokens.colorPaletteRedForeground1} data-testid="system-debug-error-circle24-regular" />;
      case 'warning':
        return <Warning24Regular primaryFill={tokens.colorPaletteYellowForeground1} data-testid="system-debug-warning24-regular" />;
      case 'testing':
        return <Spinner size="tiny" data-testid="system-debug-spinner" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const appearance = status === 'success' ? 'tint' :
    status === 'error' ? 'danger' :
    status === 'warning' ? 'warning' : 'outline';

    return (
      <Badge
        appearance={appearance as any}
        className={styles.statusBadge} data-testid="system-debug-badge">

        {status}
      </Badge>);

  };

  const getOverallStats = () => {
    let total = 0;
    let success = 0;
    let error = 0;
    let warning = 0;
    let pending = 0;

    testCategories.forEach((category) => {
      category.tests.forEach((test) => {
        total++;
        switch (test.status) {
          case 'success':success++;break;
          case 'error':error++;break;
          case 'warning':warning++;break;
          case 'pending':pending++;break;
        }
      });
    });

    return { total, success, error, warning, pending };
  };

  const stats = getOverallStats();

  return (
    <div className={styles.container} data-testid="system-debug-container-div">
      <div className={styles.header} data-testid="system-debug-header-div">
        <Title1 data-testid="system-debug-system-integration-debug-panel-title1">System Integration Debug Panel</Title1>
        <Body1 data-testid="system-debug-comprehensive-testing-of-all-api-endpoints-body1">Comprehensive testing of all API endpoints and integrations</Body1>
      </div>

      <MessageBar data-testid="system-debug-message-bar">
        <MessageBarBody data-testid="system-debug-total-message-bar-body">
          <MessageBarTitle data-testid="system-debug-test-statistics-message-bar-title">Test Statistics</MessageBarTitle>
          Total: {stats.total} | Success: {stats.success} | Error: {stats.error} | Warning: {stats.warning} | Pending: {stats.pending}
        </MessageBarBody>
      </MessageBar>

      <div className={styles.actionButtons} data-testid="system-debug-action-buttons-div">
        <Button
          appearance="primary"
          icon={<ArrowSync24Regular data-testid="system-debug-arrow-sync24-regular" />}
          onClick={runAllTests}
          disabled={isTestingAll} data-testid="system-debug-button">

          {isTestingAll ? 'Testing All...' : 'Run All Tests'}
        </Button>
        <Button
          appearance="secondary"
          onClick={resetTests}
          disabled={isTestingAll} data-testid="system-debug-reset-all-button">

          Reset All
        </Button>
      </div>

      <div className={styles.testGrid} data-testid="system-debug-test-grid-div">
        {testCategories.map((category, categoryIndex) =>
        <Card key={category.name} className={styles.testCard} data-testid={`system-debug-card-${categoryIndex}`}>
            <CardHeader
            image={<category.icon data-testid={`system-debug-category-icon-${categoryIndex}`} />}
            header={<Text weight="semibold" data-testid={`system-debug-text-${categoryIndex}`}>{category.name}</Text>}
            action={
            <Button
              appearance="subtle"
              size="small"
              onClick={() => runCategoryTests(categoryIndex)}
              disabled={isTestingAll} data-testid={`system-debug-test-all-button-${categoryIndex}`}>

                  Test All
                </Button>
            } data-testid={`system-debug-card-header-${categoryIndex}`} />

            <div className={styles.testBody} data-testid={`system-debug-test-body-div-${categoryIndex}`}>
              {category.tests.map((test, testIndex) =>
            <div key={test.endpoint} data-testid={`system-debug-test-entry-div-${categoryIndex}-${testIndex}`}>
                  <div className={styles.testItem} data-testid={`system-debug-test-item-div-${categoryIndex}-${testIndex}`}>
                    <div style={{ flex: 1 }} data-testid={`system-debug-test-details-div-${categoryIndex}-${testIndex}`}>
                      <Caption1 data-testid={`system-debug-endpoint-${categoryIndex}-${testIndex}`}>{test.endpoint}</Caption1>
                      {test.message &&
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }} data-testid={`system-debug-message-${categoryIndex}-${testIndex}`}>
                          {test.message}
                        </Caption1>
                  }
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid={`system-debug-test-actions-div-${categoryIndex}-${testIndex}`}>
                      {getStatusIcon(test.status)}
                      <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => runTest(categoryIndex, testIndex)}
                    disabled={test.status === 'testing' || isTestingAll} data-testid={`system-debug-test-button-${categoryIndex}-${testIndex}`}>

                        Test
                      </Button>
                    </div>
                  </div>
                  {test.response &&
              <div className={styles.responseBox} data-testid={`system-debug-response-box-div-${categoryIndex}-${testIndex}`}>
                      <pre data-testid={`system-debug-pre-${categoryIndex}-${testIndex}`}>{JSON.stringify(test.response, null, 2)}</pre>
                    </div>
              }
                </div>
            )}
            </div>
          </Card>
        )}
      </div>
    </div>);

};